/**
 * App — root routing + auth gate + InteriorIO chrome shell.
 *
 * The chrome (sidebar + topbar) renders with the EXACT class names
 * from the design folder
 *   `/Users/gudupuramesh/Documents/design ui/anti Interior io ui web`
 * — `.app`, `.nav`, `.nav-group`, `.nav-item`, `.main-area`,
 * `.topbar`, `.content` — so every iio.css rule applies verbatim.
 *
 * Phosphor icons load from CDN (see index.html); render them with
 * `<i className="ph ph-..." />` inline, matching the folder.
 *
 * Auth gate:
 *   - loading → spinner
 *   - signed out → SignInPage
 *   - signed in → AuthedShell with the routed page in <main class="content">
 */
import { Loader, Menu, Stack, Text } from '@mantine/core';
import { type CSSProperties, useEffect, useRef } from 'react';

import { canManageProjects, isSuperAdmin, type SidebarNavKey } from '@shared/perms';
import {
  Navigate,
  NavLink,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import { useAuth } from '@/auth/AuthProvider';
import { SignInPage } from '@/auth/SignInPage';
import { useVisibleSidebar } from '@/lib/useVisibleTabs';
import {
  ProjectHeaderProvider,
  useProjectHeader,
} from '@/components/chrome/ProjectHeaderContext';
import {
  TopbarSearchProvider,
  useTopbarSearch,
} from '@/components/chrome/TopbarSearchContext';
import { InstallPwaHint } from '@/components/InstallPwaHint';
import { signOut } from '@/lib/firebase';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
import { useCurrentUser } from '@/lib/useCurrentUser';

import { OrgSwitcher } from '@/components/OrgSwitcher';
import { PagePlaceholder } from '@/components/PagePlaceholder';
import { CategoryLibraryPage } from '@/routes/CategoryLibrary';
import { FinancePage } from '@/routes/Finance';
import { BillingPage } from '@/routes/Billing';
import { CalendarPage } from '@/routes/Calendar';
import { CreateLeadPage } from '@/routes/CreateLead';
import { EditLeadPage } from '@/routes/EditLead';
import { IOServicesPage } from '@/routes/IOServices';
import { IOServiceOrdersPage } from '@/routes/IOServiceOrders';
import { LeadsPage } from '@/routes/Leads';
import { MaterialLibraryPage } from '@/routes/MaterialLibrary';
import { CreateQuotationPage } from '@/routes/CreateQuotation';
import { DashboardPage } from '@/routes/Dashboard';
import { LedgerPage } from '@/routes/Ledger';
import { PartiesPage } from '@/routes/Parties';
import { QuotationDetailPage } from '@/routes/QuotationDetail';
import { QuotationsPage } from '@/routes/Quotations';
import { PartyDetailPage } from '@/routes/PartyDetail';
import { ProfilePage } from '@/routes/Profile';
import { ProjectDetailPage } from '@/routes/ProjectDetail';
import { ProjectEditPage } from '@/routes/ProjectEdit';
import { ProjectsPage } from '@/routes/Projects';
import { ServiceLibraryPage } from '@/routes/ServiceLibrary';
import { TeamPage } from '@/routes/Team';
import {
  ProjectLimitNotice,
  SeatLimitNotice,
  SeatPausedNotice,
  useWebSeatStatus,
} from '@/components/SeatLimitNotice';

export function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Stack mih="100vh" align="center" justify="center">
        <Loader />
        <Text size="sm" c="dimmed">
          Signing you in…
        </Text>
      </Stack>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="*" element={<Navigate to="/sign-in" replace />} />
      </Routes>
    );
  }

  return (
    <TopbarSearchProvider>
      <ProjectHeaderProvider>
        <AuthedShell />
      </ProjectHeaderProvider>
    </TopbarSearchProvider>
  );
}

// ───── Sidebar nav model (1:1 with the design folder) ─────

type NavLeaf = {
  to?: string;
  label: string;
  icon: string;
  /** Maps this nav item to a shared `SidebarNavKey` so the
   *  per-role visibility map in `@shared/perms/visibleTabs` can
   *  filter it. Items without a navKey show for every signed-in
   *  user (e.g. the universal Dashboard). */
  navKey?: SidebarNavKey;
  /** Owner-only entry — hidden from everyone except the org's
   *  Super Admin. Billing + Plans are the only items currently
   *  flagged: per `docs/roles-and-permissions.md` ('billing.manage'
   *  capability), even Admins can't subscribe — the studio owner
   *  is the billing relationship. */
  ownerOnly?: boolean;
};
type NavGroupDef = { label: string; items: NavLeaf[] };

const NAV_GROUPS: NavGroupDef[] = [
  {
    label: 'Workspace',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: 'ph ph-squares-four', navKey: 'dashboard' },
      { to: '/projects',  label: 'Projects',  icon: 'ph ph-folder-simple', navKey: 'projects' },
      { to: '/tasks',     label: 'Tasks',     icon: 'ph ph-check-square',  navKey: 'tasks' },
      { to: '/calendar',  label: 'Calendar',  icon: 'ph ph-calendar-blank', navKey: 'calendar' },
    ],
  },
  {
    label: 'Sales',
    items: [
      { to: '/leads',       label: 'Leads',      icon: 'ph ph-funnel',    navKey: 'leads' },
      { to: '/quotations',  label: 'Quotations', icon: 'ph ph-file-text', navKey: 'quotations' },
      { to: '/invoices',    label: 'Invoices',   icon: 'ph ph-receipt',   navKey: 'dashboard' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/materials',    label: 'Materials',    icon: 'ph ph-package', navKey: 'materials' },
      { to: '/services',     label: 'Services',     icon: 'ph ph-wrench',  navKey: 'services' },
      { to: '/categories',   label: 'Categories',   icon: 'ph ph-tag',     navKey: 'categories' },
    ],
  },
  {
    label: 'Studio',
    items: [
      { to: '/team',     label: 'Team',     icon: 'ph ph-users-three',   navKey: 'team' },
      { to: '/parties',  label: 'Parties',  icon: 'ph ph-address-book',  navKey: 'parties' },
      { to: '/ledger',   label: 'Ledger',   icon: 'ph ph-receipt',       navKey: 'finance' },
      { to: '/finance',  label: 'Finance',  icon: 'ph ph-wallet',        navKey: 'finance' },
      { to: '/billing',  label: 'Billing',  icon: 'ph ph-credit-card',   navKey: 'billing', ownerOnly: true },
    ],
  },
  {
    label: 'Marketplace',
    items: [
      { to: '/io-services', label: 'IO Services', icon: 'ph ph-storefront', navKey: 'platform_services' },
    ],
  },
];

function initialsOf(name: string | null | undefined): string {
  if (!name) return '·';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function AuthedShell() {
  const { user: authUser } = useAuth();
  const { userDoc } = useCurrentUser();
  const { role: orgRole } = useCurrentOrgRole();
  const isOwner = isSuperAdmin(orgRole);
  const seat = useWebSeatStatus();
  // Per-role sidebar visibility (Accountant only sees Projects /
  // Finance / Parties / Profile; Designer only sees design-stack
  // routes; etc.). Mirrors mobile's `useVisibleBottomTabs` so the
  // two surfaces stay in lock-step.
  const visibleSidebarKeys = useVisibleSidebar();
  const userData = (userDoc?.data ?? {}) as { displayName?: string; fullName?: string };
  const userName =
    userData.displayName?.trim() ||
    userData.fullName?.trim() ||
    authUser?.phoneNumber ||
    authUser?.email ||
    'You';
  // studioName moved into <OrgSwitcher /> chip — the sidebar no
  // longer renders the active studio name here directly.
  const userInitials = initialsOf(userName);

  return (
    <div className="app">
      {/* ─── Left nav (verbatim structure from projects.html) ─── */}
      <nav className="nav" aria-label="Primary navigation">
        <div className="nav-logo">
          <span className="text">
            InteriorIO<span>™</span>
          </span>
        </div>

        {NAV_GROUPS.map((g) => {
          // Two-pass filter:
          //   1. Drop owner-only items (Billing / Plans) for non-SA.
          //   2. Drop items whose `navKey` isn't in the role's
          //      visible-sidebar set. Items WITHOUT a navKey
          //      (e.g. Dashboard, Invoices) show for every role.
          const items = g.items.filter((it) => {
            if (it.ownerOnly && !isOwner) return false;
            if (it.navKey && !visibleSidebarKeys.has(it.navKey)) return false;
            return true;
          });
          if (items.length === 0) return null;
          return (
            <div className="nav-group" key={g.label}>
              <div className="nav-group-label">{g.label}</div>
              {items.map((item) => (
                <NavLink
                  key={item.label}
                  to={item.to ?? '#'}
                  end={item.to === '/dashboard'}
                  className={({ isActive }) =>
                    `nav-item${isActive && item.to ? ' active' : ''}`
                  }
                >
                  <i className={item.icon} aria-hidden />{' '}
                  <span className="text">{item.label}</span>
                </NavLink>
              ))}
            </div>
          );
        })}

        <div className="nav-spacer" />

        {/* Studio chip → opens the org switcher dialog. Stays
            above the profile link so the two affordances are
            visually separate: chip = "change which studio you're
            working in", profile link = "edit your personal info". */}
        <OrgSwitcher />

        <NavLink
          to="/profile"
          className="nav-user"
          style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
          title="Your profile"
        >
          <div className="nav-avatar">{userInitials}</div>
          <div className="nav-user-info">
            <div className="nav-user-name">{userName}</div>
            <div className="nav-user-studio">Your profile</div>
          </div>
        </NavLink>
      </nav>

      {/* ─── Main area (topbar + routed content) ─── */}
      <div className="main-area">
        <TopBar userInitials={userInitials} userName={userName} />

        <main className="content">
          {seat.isPaused ? (
            <SeatPausedNotice status={seat} />
          ) : (
          <>
          <SeatLimitNotice status={seat} />
          <ProjectLimitNotice status={seat} />
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* Live, real-data pages */}
            <Route path="/projects" element={<ProjectsPage />} />

            {/* Project edit / overview is its own page, NOT a tab.
                Literal `/edit` must come BEFORE the generic
                `/:projectId` route so React Router matches it
                first. */}
            <Route
              path="/projects/:projectId/edit"
              element={<ProjectEditPage />}
            />
            <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
            <Route path="/leads" element={<LeadsPage />} />
            {/* Order matters — `/leads/new` BEFORE `/leads/:leadId`
                so the literal route wins. */}
            <Route path="/leads/new" element={<CreateLeadPage />} />
            <Route path="/leads/:leadId/edit" element={<EditLeadPage />} />
            {/* `/leads/{id}` re-mounts the LeadsPage; it reads the
                param and auto-opens the slide-over preview panel.
                Closing the panel navigates back to `/leads`. */}
            <Route path="/leads/:leadId" element={<LeadsPage />} />

            {/* Parties — list at `/parties`, full detail page at
                `/parties/:partyId` (matches design folder's
                party-details.html). */}
            <Route path="/parties" element={<PartiesPage />} />
            <Route path="/parties/:partyId" element={<PartyDetailPage />} />
            {/* Plans are bought in the mobile app; keep old links working. */}
            <Route path="/plans" element={<Navigate to="/billing" replace />} />
            <Route path="/billing" element={<BillingPage />} />
            <Route path="/profile" element={<ProfilePage />} />

            {/* New routes — placeholders until each lands. */}
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route
              path="/tasks"
              element={
                <PagePlaceholder
                  title="Tasks"
                  body="Studio-wide tasks list across every project."
                />
              }
            />
            {/* Calendar — folds the old Appointments page in. The
                Calendar page itself handles all 4 URLs (list, create,
                preview, edit) — the create + edit forms render as
                slide-over panels per the design folder. */}
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/calendar/new" element={<CalendarPage />} />
            <Route path="/calendar/:apptId" element={<CalendarPage />} />
            <Route path="/calendar/:apptId/edit" element={<CalendarPage />} />
            {/* Quotations — list, create, edit, detail+history. Order
                matters: /quotations/new must come BEFORE /quotations/:id
                so the literal route wins. */}
            <Route path="/quotations" element={<QuotationsPage />} />
            <Route path="/quotations/new" element={<CreateQuotationPage />} />
            <Route
              path="/quotations/:quotationId/edit"
              element={<CreateQuotationPage />}
            />
            <Route
              path="/quotations/:quotationId"
              element={<QuotationDetailPage />}
            />
            <Route
              path="/invoices"
              element={
                <PagePlaceholder
                  title="Invoices"
                  body="Customer invoices issued by the studio (distinct from billing receipts)."
                />
              }
            />
            <Route path="/materials" element={<MaterialLibraryPage />} />
            <Route path="/services" element={<ServiceLibraryPage />} />
            <Route path="/categories" element={<CategoryLibraryPage />} />

            {/* IO Services — the platform-wide marketplace.
                Studios browse + place inquiry-style orders here.
                The admin side (catalogue editor + orders queue)
                lives in the separate web-admin portal at
                admin.interioros.app — keeps studio-facing routes
                clean and gates admin behind Google sign-in. Order
                matters: literal `/io-services/orders` before any
                bare `/io-services/:id` would-be detail route. */}
            <Route path="/io-services" element={<IOServicesPage />} />
            <Route path="/io-services/orders" element={<IOServiceOrdersPage />} />

            <Route path="/team" element={<TeamPage />} />
            <Route path="/ledger" element={<LedgerPage />} />
            <Route path="/finance" element={<FinancePage />} />

            {/* Removed placeholder routes (no real implementation):
                /vendors, /site-reports, /clients, /reports. Catch-all
                below sends any leftover deep link back to dashboard. */}

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </>
          )}
        </main>
      </div>
    </div>
  );
}

// ───── Top bar (verbatim structure from the design folder) ─────

// Avatar → account menu. The topbar avatar used to be a plain, inert
// <div> with no way to sign out (the only Sign-out affordances lived in
// unused chrome/TopBar.tsx + UserChip.tsx). This wraps the same
// `.topbar-avatar` element in a Mantine menu so Profile / Billing /
// Sign out are reachable from the header on every page.
function AvatarMenu({
  userInitials,
  userName,
  style,
}: {
  userInitials: string;
  userName: string;
  style?: CSSProperties;
}) {
  const navigate = useNavigate();
  const onSignOut = async () => {
    await signOut();
    navigate('/sign-in', { replace: true });
  };
  return (
    <Menu position="bottom-end" withArrow shadow="md" offset={4}>
      <Menu.Target>
        <div
          className="topbar-avatar"
          title={userName}
          role="button"
          tabIndex={0}
          aria-label="Account menu"
          style={{ cursor: 'pointer', ...style }}
        >
          {userInitials}
        </div>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>{userName}</Menu.Label>
        <Menu.Item
          leftSection={<i className="ph ph-user" />}
          onClick={() => navigate('/profile')}
        >
          Profile
        </Menu.Item>
        <Menu.Item
          leftSection={<i className="ph ph-receipt" />}
          onClick={() => navigate('/billing')}
        >
          Billing
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          color="red"
          leftSection={<i className="ph ph-sign-out" />}
          onClick={onSignOut}
        >
          Sign out
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

function TopBar({ userInitials, userName }: { userInitials: string; userName: string }) {
  const { pathname } = useLocation();
  const { query, setQuery, placeholder: ctxPlaceholder, focusToken, focus } = useTopbarSearch();
  const inputRef = useRef<HTMLInputElement | null>(null);
  // When the ProjectDetail page sets a project header via
  // `useSetProjectHeader(project)`, we swap the entire topbar
  // chrome to show the project context (back · name · subtitle ·
  // progress · Share · Edit · avatar) instead of the default
  // search + new + bell row. The page-level component never has
  // to know the topbar exists.
  const headerProject = useProjectHeader();

  const routePlaceholder = pathname.startsWith('/projects')  ? 'Search projects, clients…' :
                           pathname.startsWith('/leads')     ? 'Search leads by name, phone, email…' :
                           pathname.startsWith('/calendar')  ? 'Search appointments…' :
                           pathname.startsWith('/tasks')     ? 'Search tasks…' :
                           pathname.startsWith('/materials') ? 'Search materials…' :
                           'Search…';
  // Page-supplied placeholder beats the route default. Falls back if
  // the page hasn't set one.
  const placeholder = ctxPlaceholder && ctxPlaceholder !== 'Search…' ? ctxPlaceholder : routePlaceholder;

  // Global ⌘K / Ctrl+K → focus the search input.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().includes('MAC');
      const hit = (isMac ? e.metaKey : e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (!hit) return;
      e.preventDefault();
      focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focus]);

  // Imperative focus from any caller (incl. the ⌘K hotkey above).
  useEffect(() => {
    if (focusToken > 0) inputRef.current?.focus();
  }, [focusToken]);

  // ─── Project-context topbar variant ─────────────────────────
  // Active on /projects/:id (any tab). The page-level component
  // pushes its project doc up via `useSetProjectHeader`; we read
  // it here and render a different topbar layout.
  if (headerProject) {
    return (
      <ProjectTopBar
        project={headerProject}
        userInitials={userInitials}
        userName={userName}
      />
    );
  }

  return (
    <header className="topbar">
      <button className="mobile-menu-toggle" aria-label="Toggle navigation">
        <i className="ph ph-list" />
      </button>
      <div
        className="topbar-search"
        onClick={() => inputRef.current?.focus()}
        style={{ cursor: 'text' }}
      >
        <i className="ph ph-magnifying-glass" />
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          placeholder={placeholder}
          aria-label={placeholder}
          style={{
            flex: 1,
            border: 0,
            outline: 'none',
            background: 'transparent',
            fontSize: 13,
            color: 'var(--text-primary)',
            fontFamily: 'inherit',
            minWidth: 0,
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setQuery('');
              (e.currentTarget as HTMLInputElement).blur();
            }
          }}
        />
        {query ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setQuery('');
              inputRef.current?.focus();
            }}
            style={{
              border: 0,
              background: 'transparent',
              color: 'var(--text-tertiary)',
              cursor: 'pointer',
              padding: 2,
              display: 'inline-flex',
            }}
            aria-label="Clear search"
          >
            <i className="ph ph-x-circle" />
          </button>
        ) : (
          <kbd>⌘K</kbd>
        )}
      </div>

      <div className="topbar-spacer" />

      {/* PWA install chip — auto-hides when already installed,
          dismissed, or the browser hasn't surfaced an install
          prompt (Safari desktop / Firefox never do). */}
      <InstallPwaHint />

      {/* Global "+ New" dropdown was removed — it was a stub
          with no menu wired and added topbar noise on every
          page. Each list page (Projects, Leads, Quotations,
          Transactions, …) already has its own contextual
          "+ New X" button in-page, which is where users
          naturally look. Bring this back as a real command
          palette / quick-create menu later if needed. */}

      <button className="btn-ghost" title="Notifications" type="button">
        <i className="ph ph-bell" />
        <span className="notif-badge" />
      </button>
      <button className="btn-ghost" title="Help" type="button">
        <i className="ph ph-question" />
      </button>

      <div className="topbar-divider" />

      <AvatarMenu userInitials={userInitials} userName={userName} />
    </header>
  );
}

// ─────────────────────────────────────────────────────────────
// Project-context topbar variant
//
// Rendered in place of the default search topbar when the
// ProjectDetail page is mounted. Layout matches the design
// folder's project-details.html top bar:
//   [← back] [name + subtitle]  …  [PROGRESS ▭]  [Share] [Edit] [avatar]
// All fields are live — name + subtitle come from the project
// doc, progress is the project's `progress` field (0-100), and
// the avatar shows the signed-in user's initials.
// ─────────────────────────────────────────────────────────────

import type { ProjectListItem as _ProjectListItem } from '@/lib/useProjects';

const PROJECT_STATUS_TOPBAR_TONE: Record<
  NonNullable<_ProjectListItem['status']>,
  { bg: string; fg: string; label: string }
> = {
  active:    { bg: '#DBEAFE', fg: '#1D4ED8', label: 'ACTIVE' },
  on_hold:   { bg: '#FEF3C7', fg: '#B45309', label: 'ON HOLD' },
  completed: { bg: '#DCFCE7', fg: '#15803D', label: 'COMPLETED' },
  archived:  { bg: '#F1F5F9', fg: '#475569', label: 'ARCHIVED' },
};

function ProjectTopBar({
  project,
  userInitials,
  userName,
}: {
  project: _ProjectListItem;
  userInitials: string;
  userName: string;
}) {
  const navigate = useNavigate();
  const { role: orgRole } = useCurrentOrgRole();
  const canEdit = canManageProjects(orgRole);
  const progress = Math.max(0, Math.min(100, project.progress ?? 0));
  const status = project.status ?? 'active';
  const tone = PROJECT_STATUS_TOPBAR_TONE[status];
  const subtitleParts: string[] = [];
  if (project.location || project.siteAddress)
    subtitleParts.push(project.location || (project.siteAddress as string));
  if (project.client) subtitleParts.push(`Client: ${project.client}`);

  return (
    <header
      className="topbar"
      style={{
        gap: 16,
        paddingLeft: 16,
        paddingRight: 16,
      }}
    >
      <button
        className="mobile-menu-toggle"
        aria-label="Toggle navigation"
        type="button"
      >
        <i className="ph ph-list" />
      </button>

      {/* Back to the projects list. We navigate explicitly (not
          browser back) so a deep-linked load that lands directly
          on /projects/:id still has a sensible target. */}
      <button
        type="button"
        className="btn-ghost"
        onClick={() => navigate('/projects')}
        title="Back to Projects"
        style={{
          padding: 8,
          border: '1px solid var(--border)',
          borderRadius: 6,
          flexShrink: 0,
        }}
      >
        <i className="ph ph-arrow-left" style={{ fontSize: 16 }} />
      </button>

      {/* Identity — name + subtitle */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, minWidth: 0 }}>
        <h1
          style={{
            margin: 0,
            fontSize: 16,
            fontWeight: 700,
            color: 'var(--text-primary)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: 320,
          }}
          title={project.name}
        >
          {project.name}
        </h1>
        <span
          className="stage-pill"
          style={{
            background: tone.bg,
            color: tone.fg,
            fontWeight: 700,
            fontSize: 9,
            padding: '2px 7px',
            letterSpacing: 0.4,
            flexShrink: 0,
          }}
        >
          {tone.label}
        </span>
        {subtitleParts.length > 0 ? (
          <span
            style={{
              fontSize: 12,
              color: 'var(--text-secondary)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}
            title={subtitleParts.join(' · ')}
          >
            {subtitleParts.join(' · ')}
          </span>
        ) : null}
      </div>

      <div className="topbar-spacer" />

      {/* Progress strip — mirrors design folder's pd-header
          progress bar (120px wide, 6px tall, label + bar). */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: 'var(--text-tertiary)',
            letterSpacing: 0.5,
            textTransform: 'uppercase',
          }}
        >
          Progress
        </span>
        <div
          style={{
            width: 120,
            height: 6,
            background: '#F1F5F9',
            borderRadius: 3,
            overflow: 'hidden',
          }}
          title={`${progress}% complete`}
        >
          <div
            style={{
              width: `${progress}%`,
              height: '100%',
              background:
                progress >= 100
                  ? 'var(--success)'
                  : progress >= 70
                    ? '#2563EB'
                    : 'var(--brand)',
              transition: 'width 0.25s',
            }}
          />
        </div>
        <span
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: 'var(--text-primary)',
            fontVariantNumeric: 'tabular-nums',
            minWidth: 30,
            textAlign: 'right',
          }}
        >
          {progress}%
        </span>
      </div>

      <div className="topbar-divider" />

      {/* Share + Edit actions. Share is a future-stub (copy URL
          → clipboard for now). Edit deep-links to a settings
          slide-over the user can build out next; for v1 it just
          alerts so the button isn't a dead end. */}
      <button
        type="button"
        className="btn-secondary"
        onClick={async () => {
          try {
            await navigator.clipboard?.writeText(window.location.href);
          } catch {
            /* clipboard unavailable — silent */
          }
        }}
        style={{ height: 32, fontSize: 12, padding: '0 12px' }}
        title="Copy project URL to clipboard"
      >
        <i className="ph ph-share-network" /> Share
      </button>
      {canEdit ? (
        // Hidden for roles that can't write — Firestore rule blocks it anyway
        <button
          type="button"
          className="btn-primary"
          onClick={() => navigate(`/projects/${project.id}/edit`)}
          style={{ height: 32, fontSize: 12, padding: '0 12px' }}
          title="Edit project details · update progress · delete"
        >
          <i className="ph ph-pencil-simple" /> Edit
        </button>
      ) : null}

      <div className="topbar-divider" />

      <AvatarMenu
        userInitials={userInitials}
        userName={userName}
        style={{ width: 32, height: 32, fontSize: 12 }}
      />
    </header>
  );
}
